/* eslint-disable import/prefer-default-export */
import axios from 'axios';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { omit } from 'underscore';
import {
  RemoveNewCommentId,
  FetchCommentsRequest,
  FetchCommentsResponse,
  CreateCommentRequest,
  DeleteCommentRequest,
  UpdateCommentRequest,
  LikeCommentRequest,
  LikeResponse,
  AddCommentRequest,
  AddCommentResponse,
  EditCommentRequest,
  EditCommentResponse,
  RemoveComment,
  VoteComment,
  GetCommentLikersInfoRequest,
} from 'redux/schemas/api/comment';
import { fetchSinglePost } from 'redux/actions/posts';
import { Result } from 'redux/schemas/api';
import { Comment } from 'redux/schemas/models/comment';
import { User } from 'redux/schemas/models/my-account';
import { makeQueryParamString } from 'redux/actions/helpers';

const COMMENTS_PAGE_SIZE = 5;

const getSingleComment = (catalogId, commentId) => axios.get<Result<Comment>>(`${catalogId}/posts/${commentId}`);

export const removeNewCommentId = createAction<RemoveNewCommentId>('REMOVE_NEW_COMMENT_ID');

export const getCommentLikersInfo = createAsyncThunk<User[], GetCommentLikersInfoRequest>(
  'COMMENT_LIKERS_INFO',
  async (params) => {
    const response = await axios.get(`${params.catalogId}/posts/${params.commentId}/voters?page=${params.page}`);
    return response.data.result;
  },
);

export const addComment = createAsyncThunk<AddCommentResponse, AddCommentRequest>(
  'ADD_COMMENT',
  async (params, thunkAPI) => {
    const { catalogId, postId, commentId } = params;
    const res = await getSingleComment(catalogId, commentId);
    thunkAPI.dispatch(fetchSinglePost({ catalogId, postId, includeUserBar: true }));
    return {
      ...omit(params, 'commentId'),
      comment: res.data.result,
    };
  },
);

export const editComment = createAsyncThunk<EditCommentResponse, EditCommentRequest>(
  'EDIT_COMMENT',
  async (params) => {
    const { catalogId, commentId } = params;
    const res = await getSingleComment(catalogId, commentId);
    return { comment: res.data.result };
  },
);

export const removeComment = createAsyncThunk<RemoveComment, RemoveComment>(
  'REMOVE_COMMENT',
  async (params, thunkAPI) => {
    const { catalogId, postId } = params;
    thunkAPI.dispatch(fetchSinglePost({ catalogId, postId, includeUserBar: true }));
    return params;
  },
);

export const voteComment = createAsyncThunk<VoteComment, VoteComment>(
  'LIKE_COMMENT',
  async (params, thunkAPI) => {
    const { catalogId, commentId } = params;
    thunkAPI.dispatch(getCommentLikersInfo({
      catalogId,
      commentId,
      page: 1,
    }));
    return params;
  },
);

export const fetchComments = createAsyncThunk<FetchCommentsResponse, FetchCommentsRequest>(
  'FETCH_COMMENTS',
  async (params) => {
    const queryString = makeQueryParamString(
      omit({
        ...params,
        pageSize: params.pageSize ?? COMMENTS_PAGE_SIZE,
        requestedFrom: 'lecture_page',
      },
      ['catalogId', 'postId']),
    );
    const response = await axios.get(`${params.catalogId}/topics/${params.postId}/posts?${queryString}`);
    return response.data.result;
  },
);

export const createComment = createAsyncThunk<Comment, CreateCommentRequest>(
  'CREATE_COMMENT',
  async (params, thunkAPI) => {
    const { catalogId, postId, callback } = params;
    const response = await axios.post(`${catalogId}/topics/${postId}/posts`, { post: params });
    thunkAPI.dispatch(fetchSinglePost({ catalogId, postId, callback, includeUserBar: true }));
    return response.data.result;
  },
);

export const deleteComment = createAsyncThunk<Comment, DeleteCommentRequest>(
  'DELETE_COMMENT',
  async (params, thunkAPI) => {
    const { catalogId, postId, callback } = params;
    const response = await axios.delete(`${params.catalogId}/posts/${params.commentId}`);
    thunkAPI.dispatch(fetchSinglePost({ catalogId, postId, callback, includeUserBar: true }));
    return response.data.result;
  },
);

export const updateComment = createAsyncThunk<Comment, UpdateCommentRequest>(
  'UPDATE_COMMENT',
  async (params) => {
    const post = omit(params, ['catalogId', 'commentId']);
    const response = await axios.put(`${params.catalogId}/posts/${params.commentId}`, { post });
    return response.data.result;
  },
);

export const voteUpComment = createAsyncThunk<LikeResponse, LikeCommentRequest>(
  'VOTE_UP_COMMENT',
  async (params) => {
    const response = await axios.post(`${params.catalogId}/posts/${params.commentId}/voteup`, {});
    return response.data.result;
  },
);

export const voteDownComment = createAsyncThunk<LikeResponse, LikeCommentRequest>(
  'VOTE_DOWN_COMMENT',
  async (params) => {
    const response = await axios.post(`${params.catalogId}/posts/${params.commentId}/votedown`, {});
    return response.data.result;
  },
);
